import { currencySymbol } from "../../helper/commonHelper";
import { ACTION_ICONS, ROLE_VISIBILITY, isVisibleForRole } from "../../config/siteConfig";
import React, { useContext, useEffect, useState } from "react";
import DContext from "../../context/DContext";
import { Link } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../../components/loader/ContentLoader";
import DetailView from "../../components/location/DetailView";
import { walletTopupService } from "../../services/wallet-topup.service";
import { suppliersService } from "../../services/suppliers.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import moment from "moment";
import { fileUrl } from "../../components/common/AccountSections";
import { dropdownSelectStyles } from "../../utils/selectStyles";
import { formatDateYYYYMMDD } from "../../utils/dateUtils";

// API status value -> label shown on screen
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Accepted",
  rejected: "Rejected",
};

const STATUS_FILTER_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }));

// Table columns, in display order
const TABLE_HEADERS = ["S.No.", "Date & Time", "Company", "Receipt", "Type", "Amount", "Mode", "Status", "Action"];

const TYPE_LABEL = {
  topup: "Top-up",
  transfer: "Balance",
  withdrawal: "Withdrawal",
};

const REQUEST_TYPE_OPTIONS = [
  { value: "topup", label: "Top-up" },
  { value: "withdrawal", label: "Withdrawal" },
  { value: "transfer", label: "Transfer" },
];

const DIRECTION_OPTIONS = [
  { value: "raised", label: "Raised" },
  { value: "received", label: "Received" },
];

const WALLET_TYPE_OPTIONS = [
  { value: "agent", label: "Agent" },
  { value: "supplier", label: "Supplier" },
];

const STATUS_STYLE = {
  approved: { bg: "#d4edda", color: "#155724" },
  pending: { bg: "#fff3cd", color: "#856404" },
  rejected: { bg: "#f8d7da", color: "#721c24" },
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const fmt = (amt) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(
    Number(amt || 0),
  );

const formatDate = (d) => (d ? String(d).slice(0, 10) : "-");

const isImageFile = (url) => /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url || "");

// "neft" -> "neft", "online_transfer" -> "online transfer"
const formatMode = (mode) => (mode ? String(mode).replace(/_/g, " ") : "-");

const Wallet = () => {
  const { start, complete } = useLoadingBar();
  const { authState } = useContext(DContext);
  const showRaiseRequest = isVisibleForRole(
    ROLE_VISIBILITY.walletPage.raiseRequestButton,
    authState,
  );
  // Admin (1001): can accept / reject pending requests. Supplier (1003): view only.
  const showReviewActions = isVisibleForRole(
    ROLE_VISIBILITY.walletPage.reviewActions || [1001], // fallback if siteConfig.ts (gitignored) lacks the key
    authState,
  );
  const showAdminFilters = isVisibleForRole(
    ROLE_VISIBILITY.walletPage.adminFilters,
    authState,
  );

  const [loading, setLoading] = useState(false);
  const [walletRes, setWalletRes] = useState({ pagination: {}, requests: [] });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = {
    wallet_type: "",
    agent_code: "",
    supplier_code: "",
    payer_supplier_code: "",
    direction: "",
    request_type: "",
    from: "",
    to: "",
    status: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [pagingLimit, pageValue, appliedFilters]);

  const [supplierOptions, setSupplierOptions] = useState([]);

  useEffect(() => {
    loadSupplierOptions();
  }, []);

  const loadSupplierOptions = async () => {
    try {
      const res = await suppliersService.list({
        page: 1,
        status: "active",
      });
      setSupplierOptions(
        (res?.resData?.data || []).map((s) => ({
          value: s.supplier_code,
          label: `${s.agency_name} (${s.supplier_code})`,
        })),
      );
    } catch (err) {
      setSupplierOptions([]);
    }
  };

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      start();
      const res = await walletTopupService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setWalletRes({
          pagination: res.resData?.pagination || {},
          requests: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  // status change: opens a popup (target request + new status); submit asks for a second confirmation
  const [review, setReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    approved_amount: "",
    reviewer_remarks: "",
  });
  const [reviewing, setReviewing] = useState(false);

  const handleStatusChange = (request, newStatus) => {
    if (newStatus === request?.status) return;
    setReviewForm({
      approved_amount:
        newStatus === "approved" ? String(request?.amount ?? "") : "",
      reviewer_remarks: "",
    });
    setReview({ request, status: newStatus });
  };

  const closeReview = () => {
    if (!reviewing) setReview(null);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    const isApprove = review.status === "approved";
    if (isApprove && !(Number(reviewForm.approved_amount) > 0)) {
      toast.error("Enter a valid approved amount");
      return;
    }
    if (!reviewForm.reviewer_remarks.trim()) {
      toast.error("Remarks are required");
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isApprove ? "Approve request?" : "Reject request?",
      text: `Are you sure you want to ${isApprove ? "approve" : "reject"} ${review.request.request_ref}?`,
      icon: isApprove ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: isApprove ? "Yes, approve" : "Yes, reject",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;
    try {
      setReviewing(true);
      const remarks = reviewForm.reviewer_remarks.trim();
      const res = isApprove
        ? await walletTopupService.approve(review.request.request_ref, {
            approved_amount: Number(reviewForm.approved_amount),
            reviewer_remarks: remarks,
          })
        : await walletTopupService.reject(review.request.request_ref, {
            reviewer_remarks: remarks,
          });
      if (res?.success) {
        toast.success(res.message);
        setReview(null);
        loadRequests();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setReviewing(false);
    }
  };

  const handleViewRequest = (request) => {
    setViewRequest(request);
    setShow(true);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    //setAppliedFilters({ ...filters });
    setAppliedFilters({
            ...filters,
            from: filters.from
              ? formatDateYYYYMMDD(filters.from)
              : "",
            to: filters.to
              ? formatDateYYYYMMDD(filters.to)
              : "",
          });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const requests = walletRes?.requests || [];
  const totalRecords = walletRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const pendingCount = requests.filter((r) => r?.status === "pending").length;
  const approvedCount = requests.filter((r) => r?.status === "approved").length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Request Ref", key: "request_ref" },
    { label: "Agent Code", key: "agent_code" },
    { label: "Supplier Code", key: "supplier_code" },
    { label: "Request Type", key: "request_type" },
    { label: "Amount", key: "amount" },
    { label: "Approved Amount", key: "approved_amount" },
    { label: "Currency", key: "currency_code" },
    { label: "Payment Mode", key: "payment_mode" },
    { label: "Payment Ref", key: "payment_ref" },
    { label: "Payment Date", key: "payment_date" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const statusBadge = (status) => {
    const s = STATUS_STYLE[status] || { bg: "#e9ecef", color: "#495057" };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          background: s.bg,
          color: s.color,
          textTransform: "capitalize",
        }}
      >
        {STATUS_LABEL[status] || status || "-"}
      </span>
    );
  };

  // Fields for the "Top-up Request Details" popup. Mode specific fields are listed only for the modes that use them.
  // To add / remove a detail, edit this list.
  const getViewFields = (r) => {
    const mode = r.payment_mode;
    const hasBank = mode !== "cash_at_headoffice";
    const isCheque = mode === "cheque_deposit";
    const isHeadoffice = mode === "cash_at_headoffice";
    const dateTime = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm:ss") : "");

    return [
      { label: "Request Ref", value: r.request_ref },
      { label: "Requested By", value: r.agent_code || r.supplier_code },
      { label: "Company Name", value: r.supplier?.agency_name || r.agent?.company_name },
      { label: "Type", value: TYPE_LABEL[r.request_type] || r.request_type },
      { label: "Payment Mode", value: formatMode(mode) },
      { label: "Status", value: r.status, badge: true },
      { label: "Deposit Amount", value: r.amount ? currencySymbol(r.currency_code) + fmt(r.amount) : "" },
      { label: "Approved Amount", value: r.approved_amount ? currencySymbol(r.currency_code) + fmt(r.approved_amount) : "" },
      ...(!isCheque ? [{ label: "Deposit Date", value: r.payment_date ? formatDate(r.payment_date) : "" }] : []),
      ...(hasBank ? [
        { label: "Bank Name", value: r.bank_name || r.bank?.bank_name },
        { label: "Account Number", value: r.account_number || r.bank?.account_number },
      ] : []),
      ...(isHeadoffice ? [{ label: "Concern Person", value: r.concern_person }] : []),
      ...(isCheque ? [
        { label: "Cheque Draw on Bank", value: r.cheque_drawn },
        { label: "Cheque Issue Date", value: r.cheque_issue_date ? formatDate(r.cheque_issue_date) : "" },
        { label: "Cheque Number", value: r.cheque_number },
      ] : []),
      { label: "Transaction Id", value: r.payment_ref },
      { label: "Balance Before", value: r.balance_before },
      { label: "Balance After", value: r.balance_after },
      { label: "Requested At", value: dateTime(r.created_at) },
      { label: "Reviewed At", value: dateTime(r.reviewed_at) },
      { label: "Remark", value: r.remarks, full: true },
      { label: "Reviewer Remarks", value: r.reviewer_remarks, full: true },
    ];
  };
  // Small round icon button used in the Action column
  const actionBtn = (bg, color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    padding: 0,
  });

  const receiptCell = (request) => {
    const url = fileUrl(request?.payment_proof);
    if (!url) return <span className="text-muted">-</span>;
    return (
      <a href={url} target="_blank" rel="noreferrer" title="Open receipt">
        {isImageFile(url) ? (
          <img
            src={url}
            alt="Receipt"
            style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, border: "1px solid #e8edf5" }}
          />
        ) : (
          <span
            className="d-flex align-items-center justify-content-center"
            style={{ width: 44, height: 44, borderRadius: 8, background: "#fff1f0", color: "#d9363e", fontSize: 20 }}
          >
            <i className="bi bi-file-earmark-pdf-fill"></i>
          </span>
        )}
      </a>
    );
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "var(--page-heading)", fontSize: 20 }}
            >
              Wallet
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#475569" }}
            >
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Wallet</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Top-up Requests
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            {showRaiseRequest && (
            <Link
              to="/wallet/wallet-topup"
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{
                background: "var(--btn-bg)",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <i className="bi bi-plus-lg"></i> Raise Wallet Request
            </Link>
            )}
            <CSVLink
              data={requests}
              headers={csvHeaders}
              filename="wallet-topup-requests.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              {showAdminFilters && (
              <>
              <div className="col-6 col-md-2">
                <Select
                  options={REQUEST_TYPE_OPTIONS}
                  value={
                    REQUEST_TYPE_OPTIONS.find(
                      (o) => o.value === filters.request_type,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, request_type: o ? o.value : "" })
                  }
                  placeholder="Select Request Type"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={DIRECTION_OPTIONS}
                  value={
                    DIRECTION_OPTIONS.find(
                      (o) => o.value === filters.direction,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, direction: o ? o.value : "" })
                  }
                  placeholder="Select Direction"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={WALLET_TYPE_OPTIONS}
                  value={
                    WALLET_TYPE_OPTIONS.find(
                      (o) => o.value === filters.wallet_type,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, wallet_type: o ? o.value : "" })
                  }
                  placeholder="Select Wallet Type"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <input
                  type="text"
                  className="form-control"
                  name="agent_code"
                  placeholder="Agent code"
                  value={filters.agent_code}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={supplierOptions}
                  value={
                    supplierOptions.find(
                      (o) => o.value === filters.supplier_code,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, supplier_code: o ? o.value : "" })
                  }
                  placeholder="Select Supplier"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={supplierOptions}
                  value={
                    supplierOptions.find(
                      (o) => o.value === filters.payer_supplier_code,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({
                      ...filters,
                      payer_supplier_code: o ? o.value : "",
                    })
                  }
                  placeholder="Select Payer Supplier"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              </>
              )}
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.from || null}
                  onChange={(d) =>
                    setFilters({ ...filters, from: d ? d.format("DD-MM-YYYY") : "" })
                  }
                  format="DD-MM-YYYY"
                  placeholder="From date"
                  maxDate={filters.to || undefined}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.to || null}
                  onChange={(d) =>
                    setFilters({ ...filters, to: d ? d.format("DD-MM-YYYY") : "" })
                  }
                  format="DD-MM-YYYY"
                  placeholder="To date"
                  minDate={filters.from || undefined}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={
                    STATUS_FILTER_OPTIONS.find(
                      (o) => o.value === filters.status,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, status: o ? o.value : "" })
                  }
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={dropdownSelectStyles}
                />
              </div>
              <div
                className={
                  showAdminFilters ? "col-12 d-flex gap-2" : "col-auto d-flex gap-2"
                }
              >
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{
                    background: "var(--btn-bg)",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e8edf5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Requests
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{approvedCount}</b> Approved on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{pendingCount}</b> Pending on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {TABLE_HEADERS.map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#8a9ab5",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request, index) => (
                    <tr
                      key={request?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
                      <td className="ps-3" style={{ fontSize: 13 }}>
                        {(pageValue - 1) * Number(pagingLimit) + index + 1}
                      </td>
                      <td>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {request?.created_at ? moment(request.created_at).format("DD/MM/YYYY") : "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {request?.created_at ? moment(request.created_at).format("HH:mm:ss") : ""}
                        </div>
                      </td>
                      <td className="fw-semibold text-uppercase" style={{ fontSize: 13 }}>                        
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {request?.supplier?.agency_name || request?.agent?.company_name || "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {request?.agent_code || request?.supplier_code}
                        </div>
                      </td>                      
                      <td>{receiptCell(request)}</td>
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 12px",
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 600,
                            background: "#e7f5ff",
                            color: "#1971c2",
                            border: "1px solid #a5d8ff",
                          }}
                        >
                          {TYPE_LABEL[request?.request_type] || request?.request_type || "-"}
                        </span>
                      </td>
                      <td className="fw-semibold" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                        {currencySymbol(request?.currency_code) + Number(request?.amount || 0).toLocaleString("en-IN")}
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontSize: 12,
                            background: "#f1f3f5",
                            color: "#495057",
                          }}
                        >
                          {formatMode(request?.payment_mode)}
                        </span>
                      </td>
                      <td>{statusBadge(request?.status)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewRequest(request)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.view}`} data-action="view"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          {/* Admin only: accept / reject while the request is pending */}
                          {showReviewActions && request?.status === "pending" && (
                            <>
                              <button
                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                onClick={() => handleStatusChange(request, "approved")}
                                title="Accept"
                                style={actionBtn("#e6f7f1", "#0f8a5f")}
                              >
                                <i className="bi bi-check-lg" style={{ fontSize: 15 }}></i>
                              </button>
                              <button
                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                onClick={() => handleStatusChange(request, "rejected")}
                                title="Reject"
                                style={actionBtn("#fff0f0", "#d9363e")}
                              >
                                <i className="bi bi-x-lg" style={{ fontSize: 13 }}></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && requests.length === 0 && (
                    <tr>
                      <td
                        colSpan={TABLE_HEADERS.length}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No top-up requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> requests
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal centered show={!!review} onHide={closeReview}>
          <form onSubmit={handleReviewSubmit}>
            <Modal.Header closeButton>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {review?.status === "approved" ? "Approve" : "Reject"} Top-up
                  Request
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                  {review?.request?.request_ref} · requested{" "}
                  {fmt(review?.request?.amount)}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="row g-3">
                {review?.status === "approved" && (
                  <div className="col-12">
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: "#4b5563",
                        marginBottom: 6,
                      }}
                    >
                      Approved Amount <span className="text-danger">*</span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      value={reviewForm.approved_amount}
                      onChange={(e) =>
                        setReviewForm({
                          ...reviewForm,
                          approved_amount: e.target.value,
                        })
                      }
                      style={{
                        borderRadius: 8,
                        border: "1px solid #c3cad6",
                        fontSize: 13,
                        height: 40,
                      }}
                    />
                  </div>
                )}
                <div className="col-12">
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      color: "#4b5563",
                      marginBottom: 6,
                    }}
                  >
                    Remarks <span className="text-danger">*</span>
                  </div>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Add a note for this decision"
                    value={reviewForm.reviewer_remarks}
                    onChange={(e) =>
                      setReviewForm({
                        ...reviewForm,
                        reviewer_remarks: e.target.value,
                      })
                    }
                    style={{
                      borderRadius: 8,
                      border: "1px solid #c3cad6",
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button
                type="button"
                className="btn"
                onClick={closeReview}
                disabled={reviewing}
                style={{
                  background: "#fff",
                  border: "1px solid #c3cad6",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                  color: "#444",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white fw-semibold"
                disabled={reviewing}
                style={{
                  background: "var(--btn-bg)",
                  border: "none",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                }}
              >
                {reviewing
                  ? "Submitting..."
                  : review?.status === "approved"
                    ? "Approve Request"
                    : "Reject Request"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Top-up Request Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewRequest && (
              <>
                <DetailView fields={getViewFields(viewRequest)} />
                {fileUrl(viewRequest.payment_proof) && (
                  <div className="mt-3 p-3 bg-white" style={{ borderRadius: 14 }}>
                    <div className="mb-2" style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "#64748b" }}>
                      Receipt / Proof
                    </div>
                    <a href={fileUrl(viewRequest.payment_proof)} target="_blank" rel="noreferrer">
                      {isImageFile(viewRequest.payment_proof) ? (
                        <img
                          src={fileUrl(viewRequest.payment_proof)}
                          alt="Receipt"
                          style={{ maxWidth: "100%", maxHeight: 100, borderRadius: 10, border: "1px solid #e8edf5" }}
                        />
                      ) : (
                        <span><i className="bi bi-file-earmark-pdf-fill me-1"></i>Open proof (PDF)</span>
                      )}
                    </a>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default Wallet;
